import { Link, useLocation } from 'react-router'
import {
  GitBranch,
  Settings,
  Crown,
  ChevronLeft,
  Image,
  Video,
  UserCircle,
  Bookmark,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from '@/components/ui/sidebar'
import { Badge } from '@/components/ui/badge'
import { GenerateFormSidebar } from '@/components/generate/GenerateFormSidebar'
import { ActorsFormSidebar } from '@/components/generate/ActorsFormSidebar'
import { ImageToVideoFormSidebar } from '@/components/generate/ImageToVideoFormSidebar'
import { BlueprintSidebar } from '@/components/canvas/BlueprintSidebar'

interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  comingSoon?: boolean
  beta?: boolean
}

const FEATURE_FORM_ROUTES: Record<string, { label: string; backTo: string }> = {
  '/apps/google/generate-images': { label: 'Google · Images', backTo: '/apps' },
  '/apps/google/generate-videos': { label: 'Google · Videos', backTo: '/apps' },
  '/apps/google/generate-actors': { label: 'AI Actors', backTo: '/apps' },
  '/apps/google/generate-image-to-video': { label: 'Google · Image to Video', backTo: '/apps' },
  '/apps/studio/character': { label: 'Character', backTo: '/apps' },
  '/apps/cloudflare-ai/generate-images': { label: 'Cloudflare · Images', backTo: '/apps' },
  '/apps/replicate/generate-images': { label: 'Replicate · Images', backTo: '/apps' },
  '/apps/blueprint': { label: 'Blueprint', backTo: '/apps' },
}

const CREATE_NAV: NavItem[] = [
  { to: '/apps/google/generate-images', label: 'Google · Images', icon: Image },
  { to: '/apps/google/generate-videos', label: 'Google · Videos', icon: Video },
  { to: '/apps/google/generate-image-to-video', label: 'Google · Image to Video', icon: Video },
  { to: '/apps/cloudflare-ai/generate-images', label: 'Cloudflare · Images', icon: Image },
  {
    to: '/apps/replicate/generate-images',
    label: 'Replicate · Images',
    icon: Image,
    comingSoon: true,
  },
]

const STUDIO_NAV: NavItem[] = [
  { to: '/apps/studio/character', label: 'Character', icon: UserCircle },
]

export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const { pathname } = useLocation()

  const featureRoute = Object.entries(FEATURE_FORM_ROUTES).find(
    ([route]) => pathname === route || pathname.startsWith(route + '/'),
  )
  const isFeatureRoute = featureRoute !== undefined
  const featureLabel = featureRoute?.[1].label ?? ''
  const featureBackTo = featureRoute?.[1].backTo ?? '/'

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader className="min-h-12 justify-center border-b border-sidebar-border">
        <Link to="/apps" className="flex items-center gap-2.5 px-2 py-1">
          <SidebarLogo />
          <span className="text-sm font-semibold tracking-tight text-sidebar-foreground">
            ByokApp
          </span>
        </Link>
        {isFeatureRoute && (
          <Link
            to={featureBackTo}
            className="flex items-center gap-1 px-2 py-0.5 text-xs text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            <span className="font-medium">{featureLabel}</span>
          </Link>
        )}
      </SidebarHeader>

      {isFeatureRoute ? (
        <>
          {pathname.startsWith('/apps/studio/') || pathname === '/apps/google/generate-actors' ? (
            <ActorsFormSidebar />
          ) : pathname === '/apps/google/generate-image-to-video' ? (
            <ImageToVideoFormSidebar />
          ) : pathname.startsWith('/apps/blueprint') ? (
            <BlueprintSidebar />
          ) : (
            <GenerateFormSidebar />
          )}
        </>
      ) : (
        <SidebarContent>
          {/* Create */}
          <SidebarGroup>
            <SidebarGroupLabel>Create</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {CREATE_NAV.map((item) => (
                  <SidebarMenuItem key={item.to}>
                    {item.comingSoon ? (
                      <SidebarMenuButton disabled className="opacity-50 cursor-default">
                        <item.icon />
                        <span>{item.label}</span>
                        <Badge variant="outline" className="ml-auto h-4 px-1 text-[10px]">
                          Soon
                        </Badge>
                      </SidebarMenuButton>
                    ) : (
                      <SidebarMenuButton
                        asChild
                        isActive={pathname === item.to || pathname.startsWith(item.to + '/')}
                      >
                        <Link to={item.to}>
                          <item.icon />
                          <span>{item.label}</span>
                          {item.beta && (
                            <Badge
                              variant="outline"
                              className="ml-auto h-4 px-1 text-[10px] border-amber-500/50 text-amber-400"
                            >
                              Beta
                            </Badge>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    )}
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Studio */}
          <SidebarGroup>
            <SidebarGroupLabel>Studio</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {STUDIO_NAV.map((item) => (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      asChild
                      isActive={pathname === item.to || pathname.startsWith(item.to + '/')}
                    >
                      <Link to={item.to}>
                        <item.icon />
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Library */}
          <SidebarGroup>
            <SidebarGroupLabel>Library</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={pathname === '/apps/presets'}>
                    <Link to="/apps/presets">
                      <Bookmark />
                      <span>Presets</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Blueprints */}
          <SidebarGroup>
            <SidebarGroupLabel>Blueprints</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={pathname === '/apps/blueprints/global'}>
                    <Link to="/apps/blueprints/global">
                      <GitBranch />
                      <span>Global</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton disabled className="opacity-50 cursor-default">
                    <GitBranch />
                    <span>Mine</span>
                    <Badge variant="outline" className="ml-auto h-4 px-1 text-[10px]">
                      Soon
                    </Badge>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Bottom */}
          <SidebarGroup className="mt-auto">
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={pathname === '/apps/settings'}>
                    <Link to="/apps/settings">
                      <Settings />
                      <span>Settings</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <a
                      href={import.meta.env.VITE_STRIPE_PAYMENT_LINK ?? '#'}
                      className="text-sidebar-primary"
                    >
                      <Crown />
                      <span>Get Pro</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      )}

      <SidebarRail />
    </Sidebar>
  )
}

function SidebarLogo() {
  return (
    <svg width="22" height="22" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="6" fill="#09090b" />
      <rect x="8" y="8" width="7" height="7" rx="2" fill="#c084fc" />
      <rect x="17" y="8" width="7" height="7" rx="2" fill="#c084fc" opacity="0.5" />
      <rect x="8" y="17" width="7" height="7" rx="2" fill="#c084fc" opacity="0.5" />
      <rect x="17" y="17" width="7" height="7" rx="2" fill="#c084fc" />
    </svg>
  )
}
